import ContentHeader from '@/components/ContentHeader';
import Cta from '@/components/Cta';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { CDN2 } from '@/helpers/api';
import usePrefix from '@/hooks/usePrefix';
import { basicPageProps } from '@/utils/isServer';
import { LDJSON_FAQ } from '@/utils/ldjson';
import { Collapse, Space } from 'antd';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import Image from 'next/image';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next/';

const { Panel } = Collapse;

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { airports } = await basicPageProps(ctx);

  return {
    props: {
      airports,
    },
  };
};

export default function Faq() {
  const prefix = usePrefix();
  const [currentStep, setCurrentStep] = useState<number | null>(null);
  function handleTermsCollapseOnChange(current) {
    setCurrentStep(current);
  }
  const { t } = useTranslation();

  return (
    <div>
      <Head>
        <title>
          Airport Parking Frequently Asked Questions | On Air Parking
        </title>
        <meta
          name="description"
          content="You may be wondering where you'll parking in DIA, PIT, LGA, JAX, ATL, BOS, or PVD. If that's your question, it's also great. At On Air Parking, we negotiate the best possible deals in your airport parking market with the best parking providers. Learn more."
          key="description"
        />
        <meta
          property="og:url"
          content="https://www.onairparking.com/faq"
          key="og:url"
        />
        <meta property="og:type" content="website" key="og:type" />
        <meta
          property="og:title"
          content="Airport Parking Frequently Asked Questions | On Air Parking"
          key="og:title"
        />
        <meta
          property="og:description"
          content="You may be wondering where you'll parking in DIA, PIT, LGA, JAX, ATL, BOS, or PVD. If that's your question, it's also great."
          key="og:description"
        />
        <meta
          property="og:image"
          content={CDN2() + '/staticmyapp/oglogo-white.png'}
          key="og:image"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(LDJSON_FAQ) }}
        ></script>
      </Head>
      <ContentHeader btitle={t('faq')} stitle={t('faq_description')} />
      <LanguageSwitcher extraClass="mt-2" />
      <div className="bg-white">
        <div className="container max-w-4xl px-4 mx-auto py-12">
          <Space direction="vertical" size="middle" className="w-full ">
            <Collapse
              accordion
              activeKey={currentStep}
              onChange={handleTermsCollapseOnChange}
            >
              <Panel
                className="bg-clear-orange"
                header={
                  <div className="text-sm text-primary flex flex-row items-start w-full relative">
                    <span className="ml-2 items-start">
                      {t('faq_question_1')}
                    </span>
                  </div>
                }
                key="1"
                showArrow={true}
              >
                <p>{t('faq_answer_1')}</p>
              </Panel>
            </Collapse>
            <Collapse
              accordion
              activeKey={currentStep}
              onChange={handleTermsCollapseOnChange}
            >
              <Panel
                className="bg-clear-orange"
                header={
                  <div className="text-sm text-primary flex flex-row items-start w-full relative">
                    <span className="ml-2 items-start">
                      {t('faq_question_2')}
                    </span>
                  </div>
                }
                key="2"
                showArrow={true}
              >
                <p>{t('faq_answer_2')}</p>
              </Panel>
            </Collapse>
            <Collapse
              accordion
              activeKey={currentStep}
              onChange={handleTermsCollapseOnChange}
            >
              <Panel
                className="bg-clear-orange"
                header={
                  <div className="text-sm text-primary flex flex-row items-start w-full relative">
                    <span className="ml-2 items-start">
                      {t('faq_question_3')}
                    </span>
                  </div>
                }
                key="3"
                showArrow={true}
              >
                <p>{t('faq_answer_3')}</p>
              </Panel>
            </Collapse>
            <Collapse
              accordion
              activeKey={currentStep}
              onChange={handleTermsCollapseOnChange}
            >
              <Panel
                className="bg-clear-orange"
                header={
                  <div className="text-sm text-primary flex flex-row items-start w-full relative">
                    <span className="ml-2 items-start">
                      {t('faq_question_4')}
                    </span>
                  </div>
                }
                key="4"
                showArrow={true}
              >
                <div>
                  <p>{t('faq_answer_4')}</p>
                  <div className="flex flex-row gap-8 mt-1">
                    <div className="flex flex-row gap-2">
                      <Image
                        src={CDN2() + '/staticmyapp/shuttle-frequency-ico.svg'}
                        width={22}
                        height={22}
                        alt="Shuttle Frequency"
                      />
                      <p>{t('free_shuttle')}</p>
                    </div>
                    <div className="flex flex-row gap-2">
                      <Image
                        src={CDN2() + '/staticmyapp/distance-ico.svg'}
                        width={22}
                        height={22}
                        alt="Distance"
                      />
                      <p>{t('distance')}</p>
                    </div>
                  </div>
                </div>
              </Panel>
            </Collapse>
            <Collapse
              accordion
              activeKey={currentStep}
              onChange={handleTermsCollapseOnChange}
            >
              <Panel
                className="bg-clear-orange"
                header={
                  <div className="text-sm text-primary flex flex-row items-start w-full relative">
                    <span className="ml-2 items-start">
                      {t('faq_question_5')}
                    </span>
                  </div>
                }
                key="5"
                showArrow={true}
              >
                <p>{t('faq_answer_5')}</p>
              </Panel>
            </Collapse>
            <Collapse
              accordion
              activeKey={currentStep}
              onChange={handleTermsCollapseOnChange}
            >
              <Panel
                className="bg-clear-orange"
                header={
                  <div className="text-sm text-primary flex flex-row items-start w-full relative">
                    <span className="ml-2 items-start">
                      {t('faq_question_6')}
                    </span>
                  </div>
                }
                key="6"
                showArrow={true}
              >
                <p>{t('faq_answer_6')}</p>
              </Panel>
            </Collapse>
            <Collapse
              accordion
              activeKey={currentStep}
              onChange={handleTermsCollapseOnChange}
            >
              <Panel
                className="bg-clear-orange"
                header={
                  <div className="text-sm text-primary flex flex-row items-start w-full relative">
                    <span className="ml-2 items-start">
                      {t('faq_question_7')}
                    </span>
                  </div>
                }
                key="7"
                showArrow={true}
              >
                <p>{t('faq_answer_7')}</p>
              </Panel>
            </Collapse>
            <Collapse
              accordion
              activeKey={currentStep}
              onChange={handleTermsCollapseOnChange}
            >
              <Panel
                className="bg-clear-orange"
                header={
                  <div className="text-sm text-primary flex flex-row items-start w-full relative">
                    <span className="ml-2 items-start">
                      {t('faq_question_8')}
                    </span>
                  </div>
                }
                key="8"
                showArrow={true}
              >
                <p>{t('faq_answer_8')}</p>
              </Panel>
            </Collapse>
            <Collapse
              accordion
              activeKey={currentStep}
              onChange={handleTermsCollapseOnChange}
            >
              <Panel
                className="bg-clear-orange"
                header={
                  <div className="text-sm text-primary flex flex-row items-start w-full relative">
                    <span className="ml-2 items-start">
                      {t('faq_question_9')}
                    </span>
                  </div>
                }
                key="9"
                showArrow={true}
              >
                <p>
                  {t('faq_answer_9')}(
                  <a href="tel:8884872754" className="text-primary underline">
                    888-487-2754
                  </a>
                  ) {t('faq_answer_9_2')}{' '}
                  <a
                    href="mailto:hello@noson.io"
                    className="text-primary underline"
                  >
                    hello@noson.io
                  </a>
                  {t('faq_answer_9_3')}.
                </p>
              </Panel>
            </Collapse>
            <Collapse
              accordion
              activeKey={currentStep}
              onChange={handleTermsCollapseOnChange}
            >
              <Panel
                className="bg-clear-orange"
                header={
                  <div className="text-sm text-primary flex flex-row items-start w-full relative">
                    <span className="ml-2 items-start">
                      {t('faq_question_10')}
                    </span>
                  </div>
                }
                key="10"
                showArrow={true}
              >
                <p className="mb-4">{t('faq_answer_10')}</p>
                <ol className="list-inside list-decimal">
                  <li>
                    {t('faq_answer_10_2')}{' '}
                    <a
                      href="https://www.onairparking.com/contact"
                      className="text-primary underline"
                    >
                      https://www.onairparking.com/contact
                    </a>{' '}
                    {t('faq_answer_10_3')}{' '}
                  </li>
                  <li>{t('faq_answer_10_4')} </li>
                </ol>
              </Panel>
            </Collapse>

            <Collapse
              accordion
              activeKey={currentStep}
              onChange={handleTermsCollapseOnChange}
            >
              <Panel
                className="bg-clear-orange"
                header={
                  <div className="text-sm text-primary flex flex-row items-start w-full relative">
                    <span className="ml-2 items-start">
                      {t('faq_question_11')}
                    </span>
                  </div>
                }
                key="11"
                showArrow={true}
              >
                <p className="mb-4">{t('faq_answer_11')}</p>
              </Panel>
            </Collapse>
          </Space>
          <div className="flex flex-row justify-between items-center mt-8 mb-4">
            <h2 className={'text-lg font-bold text-left'}>
              {t('parking_protection')}
            </h2>
            <Image
              src={CDN2() + '/staticmyapp/park-guard.png'}
              width={70}
              height={40}
              alt="Park Guard Logo"
            />
          </div>

          <Space direction="vertical" size="middle" className="w-full ">
            <Collapse
              accordion
              activeKey={currentStep}
              onChange={handleTermsCollapseOnChange}
            >
              <Panel
                className="bg-clear-orange"
                header={
                  <div className="text-sm text-primary flex flex-row items-start w-full relative">
                    <span className="ml-2 items-start">
                      {t('faq_question_12')}
                    </span>
                  </div>
                }
                key="12"
                showArrow={true}
              >
                <p>{t('faq_answer_12')}</p>
              </Panel>
            </Collapse>
            <Collapse
              accordion
              activeKey={currentStep}
              onChange={handleTermsCollapseOnChange}
            >
              <Panel
                className="bg-clear-orange"
                header={
                  <div className="text-sm text-primary flex flex-row items-start w-full relative">
                    <span className="ml-2 items-start">
                      {t('faq_question_13')}
                    </span>
                  </div>
                }
                key="13"
                showArrow={true}
              >
                <p>
                  {t('faq_answer_13_1')}{' '}
                  <a
                    href="https://www.parkguardclaims.com/OnAirParking"
                    className="text-primary underline"
                    target="_blank"
                  >
                    https://www.parkguardclaims.com/OnAirParking
                  </a>{' '}
                  {t('faq_answer_13_2')}
                </p>
              </Panel>
            </Collapse>
            <Collapse
              accordion
              activeKey={currentStep}
              onChange={handleTermsCollapseOnChange}
            >
              <Panel
                className="bg-clear-orange"
                header={
                  <div className="text-sm text-primary flex flex-row items-start w-full relative">
                    <span className="ml-2 items-start">
                      {t('faq_question_14')}
                    </span>
                  </div>
                }
                key="14"
                showArrow={true}
              >
                <p>{t('faq_answer_14')}</p>
              </Panel>
            </Collapse>
            <Collapse
              accordion
              activeKey={currentStep}
              onChange={handleTermsCollapseOnChange}
            >
              <Panel
                className="bg-clear-orange"
                header={
                  <div className="text-sm text-primary flex flex-row items-start w-full relative">
                    <span className="ml-2 items-start">
                      {t('faq_question_15')}
                    </span>
                  </div>
                }
                key="15"
                showArrow={true}
              >
                <div>
                  <p>{t('faq_answer_15_1')} </p>
                  <ul className="list-disc list-inside px-4 my-2">
                    <li>{t('faq_answer_15_2')}</li>

                    <li>{t('faq_answer_15_3')}</li>

                    <li>{t('faq_answer_15_4')}</li>
                  </ul>
                  <p>
                    {t('faq_answer_15_5')}{' '}
                    <a
                      href="https://www.parkguard.com/terms-of-use-onair-parking"
                      className="text-primary underline"
                      target="_blank"
                    >
                      www.parkguard.com/terms-of-use-onair-parking
                    </a>
                  </p>
                </div>
              </Panel>
            </Collapse>
            <Collapse
              accordion
              activeKey={currentStep}
              onChange={handleTermsCollapseOnChange}
            >
              <Panel
                className="bg-clear-orange"
                header={
                  <div className="text-sm text-primary flex flex-row items-start w-full relative">
                    <span className="ml-2 items-start">
                      {t('faq_question_16')}
                    </span>
                  </div>
                }
                key="16"
                showArrow={true}
              >
                <p>{t('faq_answer_16')}</p>
              </Panel>
            </Collapse>
            <Collapse
              accordion
              activeKey={currentStep}
              onChange={handleTermsCollapseOnChange}
            >
              <Panel
                className="bg-clear-orange"
                header={
                  <div className="text-sm text-primary flex flex-row items-start w-full relative">
                    <span className="ml-2 items-start">
                      {t('faq_question_17')}{' '}
                    </span>
                  </div>
                }
                key="17"
                showArrow={true}
              >
                <p>{t('faq_answer_17_1')}</p>
                <ul className="list-disc list-inside px-4 my-2">
                  <li>{t('faq_answer_17_2')}</li>
                  <li>{t('faq_answer_17_3')}</li>
                  <li>{t('faq_answer_17_4')}</li>
                </ul>
              </Panel>
            </Collapse>
            <Collapse
              accordion
              activeKey={currentStep}
              onChange={handleTermsCollapseOnChange}
            >
              <Panel
                className="bg-clear-orange"
                header={
                  <div className="text-sm text-primary flex flex-row items-start w-full relative">
                    <span className="ml-2 items-start">
                      {t('faq_question_18')}{' '}
                    </span>
                  </div>
                }
                key="18"
                showArrow={true}
              >
                <p>{t('faq_answer_18')}</p>
              </Panel>
            </Collapse>
            <Collapse
              accordion
              activeKey={currentStep}
              onChange={handleTermsCollapseOnChange}
            >
              <Panel
                className="bg-clear-orange"
                header={
                  <div className="text-sm text-primary flex flex-row items-start w-full relative">
                    <span className="ml-2 items-start">
                      {t('faq_question_19')}{' '}
                    </span>
                  </div>
                }
                key="19"
                showArrow={true}
              >
                <p>{t('faq_answer_19')}</p>
              </Panel>
            </Collapse>
            <Collapse
              accordion
              activeKey={currentStep}
              onChange={handleTermsCollapseOnChange}
            >
              <Panel
                className="bg-clear-orange"
                header={
                  <div className="text-sm text-primary flex flex-row items-start w-full relative">
                    <span className="ml-2 items-start">
                      {t('faq_question_20')}{' '}
                    </span>
                  </div>
                }
                key="20"
                showArrow={true}
              >
                <p>{t('faq_answer_20')}</p>
              </Panel>
            </Collapse>
          </Space>

          <div className="flex flex-col max-w-xl border align-center items-center border-primary rounded-md min-h-64 h-full pt-8 pb-8 mx-auto mt-12">
            <h3 className="font-medium text-xl mb-4">{t('any_additional')}</h3>
            <Cta
              external={false}
              buttonText={t('contact_us')}
              url={prefix + '/contact'}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
